import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../lib/firebase';
import { doc, getDoc, updateDoc, deleteField } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { FaUserCircle } from "react-icons/fa";
import '../styles/Wallets.css';
import Loader from '../components/Loader';

const Accounts = () => {
    const navigate = useNavigate();
    const [accounts, setAccounts] = useState({});
    const [loading, setLoading] = useState(true);
    const [editMode, setEditMode] = useState({});
    const [tempValues, setTempValues] = useState({});
    const [savingAccount, setSavingAccount] = useState(null);
    const [deletingAccount, setDeletingAccount] = useState(null);
    const [currentUserRole, setCurrentUserRole] = useState(null);
    const [authLoading, setAuthLoading] = useState(true);

    const [showCreateForm, setShowCreateForm] = useState(false);
    const [newAccount, setNewAccount] = useState({ accountName: '', bankName: '', accountNumber: '' });
    const [creating, setCreating] = useState(false);

    useEffect(() => {
        const fetchCurrentUserRole = async () => {
            const auth = getAuth();
            const currentUser = auth.currentUser;

            if (!currentUser) {
                setCurrentUserRole("unauthenticated");
                setAuthLoading(false);
                return;
            }

            try {
                const currentUserDoc = await getDoc(doc(db, "users", currentUser.uid));
                if (currentUserDoc.exists()) {
                    const role = currentUserDoc.data().role || "user";
                    setCurrentUserRole(role);
                } else {
                    setCurrentUserRole("unknown");
                }
            } catch (error) {
                console.error("Error fetching current user role:", error);
                setCurrentUserRole("error");
            } finally {
                setAuthLoading(false);
            }
        };

        fetchCurrentUserRole();
    }, []);

    useEffect(() => {
        const fetchAccountData = async () => {
            try {
                const docRef = doc(db, 'users', 'accounts');
                const docSnap = await getDoc(docRef);
                if (docSnap.exists()) {
                    const data = docSnap.data();
                    setAccounts(data);
                    setTempValues(data);
                } else {
                    console.error("No accounts document found!");
                }
            } catch (err) {
                console.error("Error fetching account data:", err);
            } finally {
                setLoading(false);
            }
        };

        fetchAccountData();
    }, []);

    const toggleEdit = (key) => {
        setEditMode(prev => ({ ...prev, [key]: !prev[key] }));
        // Reset tempValues for this account when toggling edit off
        if (editMode[key]) {
            setTempValues(prev => ({
                ...prev,
                [key]: accounts[key]
            }));
        }
    };

    const handleChange = (e, key, field) => {
        setTempValues(prev => ({
            ...prev,
            [key]: {
                ...prev[key],
                [field]: e.target.value
            }
        }));
    };

    const saveAccount = async (key) => {
        setSavingAccount(key);
        try {
            const updated = {
                ...accounts,
                [key]: {
                    ...accounts[key],
                    accountName: tempValues[key].accountName,
                    bankName: tempValues[key].bankName,
                }
            };

            await updateDoc(doc(db, 'users', 'accounts'), updated);
            setAccounts(updated);
            toggleEdit(key);
        } catch (error) {
            console.error('Error updating account:', error);
        } finally {
            setSavingAccount(null);
        }
    };

    const deleteAccount = async (key) => {
        setDeletingAccount(key);
        try {
            const accountDocRef = doc(db, 'users', 'accounts');

            // Remove key from Firestore
            await updateDoc(accountDocRef, {
                [key]: deleteField(),
            });

            // Remove key from local state
            const updatedAccounts = { ...accounts };
            delete updatedAccounts[key];
            setAccounts(updatedAccounts);
            setTempValues(updatedAccounts);

            setEditMode(prev => {
                const copy = { ...prev };
                delete copy[key];
                return copy;
            });
        } catch (error) {
            console.error('Error deleting account:', error);
        } finally {
            setDeletingAccount(null);
        }
    };

    const handleGoBack = () => {
        navigate(-1);
    };

    const handleCreateAccount = async () => {
        const trimmedAccountName = newAccount.accountName.trim();
        const bankName = newAccount.bankName.trim();
        const accountNumber = newAccount.accountNumber.trim();

        if (!trimmedAccountName || !bankName || !accountNumber) return;

        setCreating(true);
        try {
            const newKey = accountNumber;

            const newAccountEntry = {
                ...accounts,
                [newKey]: {
                    accountName: trimmedAccountName,
                    bankName,
                }
            };

            await updateDoc(doc(db, 'users', 'accounts'), newAccountEntry);
            setAccounts(newAccountEntry);
            setTempValues(newAccountEntry);
            setNewAccount({ accountName: '', bankName: '', accountNumber: '' });
            setShowCreateForm(false);
        } catch (error) {
            console.error('Error creating new account:', error);
        } finally {
            setCreating(false);
        }
    };

    if (loading || authLoading) return <Loader />;

    if (currentUserRole !== "admin") {
        return (
            <div className="web3-user-not-found">
                <FaUserCircle size={48} />
                <h2>Access Denied</h2>
                <div onClick={handleGoBack} className="route-Links">
                    Go Back
                </div>
            </div>
        );
    }

    const sortedAccountEntries = Object.entries(accounts)
        .filter(([key]) => /^\d+$/.test(key) || /^account\d+$/.test(key))
        .sort((a, b) => a[0].localeCompare(b[0]));

    return (
        <div className="wallets-container">
            <h2>Account Addresses</h2>

            {/* Create Account Section */}
            <div>
                {showCreateForm ? (
                    <div
                        className="modal-overlay"
                        onClick={() => setShowCreateForm(false)}
                    >
                        <div
                            className="plan-create-form"
                            onClick={(e) => e.stopPropagation()}
                        >
                    <div className="plan-modal-body">
                                <div><h2>Add a New Account</h2></div>
                                <div>
                                    <label>Enter Account Name</label>
                                    <input
                                        className="input-field"
                                        type="text"
                                        placeholder="Account Name (e.g. John Doe)"
                                        value={newAccount.accountName}
                                        onChange={(e) => setNewAccount({ ...newAccount, accountName: e.target.value })}
                                    />
                                </div>
                                <div>
                                    <label>Enter Bank Name</label>
                                    <input
                                        className="input-field"
                                        type="text"
                                        placeholder="Bank Name (e.g. Chase)"
                                        value={newAccount.bankName}
                                        onChange={(e) => setNewAccount({ ...newAccount, bankName: e.target.value })}
                                    />
                                </div>
                                <div>
                                    <label>Enter Account Number</label>
                                    <input
                                        className="input-field"
                                        type="text"
                                        placeholder="Account Number (e.g. 1234567890)"
                                        value={newAccount.accountNumber}
                                        onChange={(e) => setNewAccount({ ...newAccount, accountNumber: e.target.value })}
                                    />
                                </div>
                                <div className="wallet-actions">
                                    <button className='action-btn trade' onClick={handleCreateAccount} disabled={creating}>
                                        {creating ? "Creating..." : "Create"}
                                    </button>
                                    <button
                                        className='action-btn withdraw'
                                        onClick={() => setShowCreateForm(false)}
                                        disabled={creating}
                                    >
                                        Cancel
                                    </button>
                                </div>
                    </div>
                        </div>
                    </div>
                ) : (
                    <button
                        className="create-btn"
                        onClick={() => setShowCreateForm(true)}
                        style={{ marginTop: "10px" }}
                    >
                        + Add New Account
                    </button>
                )}
            </div>


            {sortedAccountEntries.map(([key, { accountName, bankName }]) => (
                <div key={key} className="wallet-box">
                    <div className="wallet-top-row">
                        <div className="wallet-right">
                            <div className="wallet-header">
                                <h3>{accountName.toUpperCase()}</h3>
                            </div>

                            {editMode[key] ? (
                                <>
                                    <label>Account Name</label>
                                    <input
                                        className="input-field"
                                        type="text"
                                        value={tempValues[key]?.accountName || ''}
                                        onChange={(e) => handleChange(e, key, 'accountName')}
                                        placeholder="Account Name"
                                    />
                                    <label>Bank Name</label>
                                    <input
                                        className="input-field"
                                        type="text"
                                        value={tempValues[key]?.bankName || ''}
                                        onChange={(e) => handleChange(e, key, 'bankName')}
                                        placeholder="Bank Name"
                                    />
                                    <div className="plan-actions">
                                        <button
                                            className='action-btn trade'
                                            onClick={() => saveAccount(key)}
                                            disabled={savingAccount === key}
                                        >
                                            {savingAccount === key ? 'Saving...' : 'Save'}
                                        </button>
                                        <button
                                            className='action-btn withdraw'
                                            onClick={() => toggleEdit(key)}
                                            disabled={savingAccount === key}
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <p className="wallet-info"><strong>Account Number:</strong> {key}</p>
                                    <p className="wallet-info"><strong>Bank:</strong> {bankName}</p>
                                    <div className="plan-actions">
                                        <button
                                            onClick={() => toggleEdit(key)}
                                        >
                                            Edit
                                        </button>
                                        <button
                                            onClick={() => deleteAccount(key)}
                                            disabled={deletingAccount === key}
                                        >
                                            {deletingAccount === key ? 'Deleting...' : 'Delete'}
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            ))}

        </div>
    );
};

export default Accounts;